import { Template } from '../domain/entities/template';
import React, { createContext, FC, useEffect, useState } from 'react';
import { useInitialState } from './InitialStateContext';
import usePreviousDistinct from '../hooks/usePreviousDistinct';
import { useFedopsLogger } from './FedopsLoggerContext';
import { HttpTemplateRepository } from '../domain/repositories/HttpTemplateRepository';
import { createHttpClient } from '@wix/http-client';

type TemplatesRepositoryContextValue = {
  templates?: Template[];
};

export type TemplatesRepositoryContextProviderProps = {
  categoryId?: string;
};

const TemplatesRepositoryContext =
  createContext<TemplatesRepositoryContextValue | null>(null);
export const TemplatesRepositoryContextProvider: FC<
  TemplatesRepositoryContextProviderProps
> = (props) => {
  const { categoryId, children } = props;
  const initialState = useInitialState();
  const prevCategoryId = usePreviousDistinct(categoryId);
  const [templates, setTemplates] = useState<Template[] | undefined>(
    initialState.templates,
  );
  const fedopsLogger = useFedopsLogger();

  useEffect(() => {
    const actualPrevCategoryId = prevCategoryId ?? initialState.categoryId;
    if (!categoryId || categoryId === actualPrevCategoryId) {
      return;
    }

    const fetchTemplates = async () => {
      const templateRepository = new HttpTemplateRepository(createHttpClient());
      const domainTemplates = await templateRepository.loadTemplates({
        categoryId,
        language: initialState.locale,
      });

      setTemplates(domainTemplates);
      fedopsLogger.stopActiveInteraction();
    };

    void fetchTemplates();
  }, [
    categoryId,
    fedopsLogger,
    initialState.categoryId,
    initialState.locale,
    prevCategoryId,
  ]);

  return (
    <TemplatesRepositoryContext.Provider
      value={{
        templates,
      }}
    >
      {children}
    </TemplatesRepositoryContext.Provider>
  );
};

export const useTemplatesRepositoryContext = () => {
  const context = React.useContext(TemplatesRepositoryContext);
  if (!context) {
    throw new Error(
      'useTemplateContext must be used within a TemplateContextProvider',
    );
  }

  return context;
};
